// メインアプリケーション（統合ファイル）
import type { SettingsData } from './types/index.js';

import { setupErrorHandlers } from './utils/error-handler.js';
import { setLanguage, getCurrentLanguage, initI18n, setSettingsData, t } from './utils/i18n.js';
import settingsData from './config/settings.json';
import { AD_CONFIG } from './config/ads-config';
import {
    setSettings
} from './state/app-state.js';

// UIハンドラー
import {
    toggleSection,
    toggleCategory,
    togglePreview,
    toggleAllSections,
    renderUI,
    updateUITexts
} from './handlers/ui-handlers.js';

// プレビューハンドラー
import {
    restorePreviewHeightLocal,
    startPreviewResize
} from './handlers/preview-handlers.js';

// ストレージハンドラー
import {
    restoreSelectionStateLocal,
    restoreCategoryStateLocal,
    saveLeftScrollLocal,
    restoreLeftScrollLocal,
    saveVariableValueLocal,
    restoreVariableValuesLocal
} from './handlers/storage-handlers.js';

// コードハンドラー
import {
    updateCode,
    updateCodePreview
} from './handlers/code-handlers.js';

// ファイルハンドラー
import {
    copyToClipboard,
    downloadBatchFile,
    downloadRevertBatchFile
} from './handlers/file-handlers.js';

// アプリハンドラー
import {
    importTreeOutput,
    clearApps
} from './handlers/app-handlers.js';

// 検索ハンドラー
import {
    filterSettings
} from './handlers/search-handlers.js';

// 広告ハンドラー
import {
    loadAds
} from './handlers/ads-handlers.js';

// ドキュメントハンドラー
import {
    openDocs,
    closeDocs
} from './handlers/docs-handlers.js';

// NICハンドラー
import {
    addNIC,
    removeNIC,
    updateNICConfig,
    updateGatewayPlaceholder,
    showGatewayAutocomplete,
    hideGatewayAutocomplete,
    applyGatewayAutocomplete,
    handleGatewayKeyDown
} from './handlers/nic-handlers.js';

// 選択ハンドラー
import {
    clearAllSelections
} from './handlers/selection-handlers.js';

// テーブルハンドラー
import {
    addRow,
    removeRow,
    updatePCName,
    updateCheckbox,
    updateVariable,
    clearTableData,
    openTableEditor,
    closeTableEditor,
    selectPC,
    updateCurrentPCIndicator,
    renderTable,
    restoreMainViewToDefault,
    CURRENT_PC_STORAGE_KEY,
    TABLE_DATA_STORAGE_KEY,
    getCurrentPCRowId,
    selectPreviousPC,
    selectNextPC
} from './handlers/table-handlers.js';

// テーブルバッチ生成ハンドラー
import {
    generateBatchFilesFromTable
} from './handlers/table-batch-handlers.js';

/**
 * 設定データを読み込む（現在は直接インポート、将来的にDBから取得する場合は非同期で変更可能）
 * @returns {Promise<Object>} 設定データ
 */
async function loadSettings(): Promise<SettingsData> {
    try {
        // 現在は直接インポートしたデータを使用
        // 将来的にDBから取得する場合は、ここを非同期で変更
        const data = settingsData as SettingsData;
        
        // 後方互換性のため、sectionsプロパティを追加
        if (data.categories && !data.sections) {
            data.sections = [];
            data.categories.forEach(category => {
                category.sections.forEach(section => {
                    data.sections!.push(section);
                });
            });
        }
        
        // i18nに設定データを設定
        setSettingsData(data);
        
        return data;
    } catch (error) {
        console.error('設定データの読み込みエラー:', error);
        throw error;
    }
}

/**
 * アプリケーション初期化
 */
export async function init() {
    setupErrorHandlers();
    
    // i18nを初期化
    initI18n();
    
    // 設定データを読み込む
    try {
        const loadedSettings = await loadSettings();
        setSettings(loadedSettings);
    } catch (error) {
        console.error('設定データの読み込みに失敗しました:', error);
        alert('設定データの読み込みに失敗しました。ページを再読み込みしてください。');
        return;
    }
    
    renderUI();
    // 永続化された状態を復元（チェック / カテゴリ開閉 / スクロール / 変数）
    restoreSelectionStateLocal();
    restoreCategoryStateLocal();
    restoreLeftScrollLocal();
    // 変数の復元は少し遅延させてDOMが完全に構築された後に実行
    setTimeout(() => {
        restoreVariableValuesLocal();
    }, 100);
    
    // 検索機能
    const searchInput = document.getElementById('searchInput') as HTMLInputElement | null;
    if (searchInput) {
        searchInput.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            filterSettings(target.value);
        });
    }
    
    // 全て展開ボタン
    const expandAllBtn = document.getElementById('expandAllButton');
    if (expandAllBtn) {
        expandAllBtn.addEventListener('click', () => {
            toggleAllSections(true);
        });
    }
    
    // 全て折りたたみボタン
    const collapseAllBtn = document.getElementById('collapseAllButton');
    if (collapseAllBtn) {
        collapseAllBtn.addEventListener('click', () => {
            toggleAllSections(false);
        });
    }

    // 全てのチェックを外すボタン
    const clearSelectionBtn = document.getElementById('clearSelectionButton');
    if (clearSelectionBtn) {
        clearSelectionBtn.addEventListener('click', () => {
            clearAllSelections();
        });
    }
    
    // コピーボタン
    const copyBtn = document.getElementById('copyButton');
    if (copyBtn) {
        copyBtn.addEventListener('click', copyToClipboard);
    }
    
    // ダウンロードボタン
    const downloadBtn = document.getElementById('downloadButton');
    if (downloadBtn) {
        downloadBtn.addEventListener('click', downloadBatchFile);
    }
    
    // 元に戻すバッチダウンロードボタン
    const downloadRevertBtn = document.getElementById('downloadRevertButton');
    if (downloadRevertBtn) {
        downloadRevertBtn.addEventListener('click', downloadRevertBatchFile);
    }
    
    // プレビューペインの折りたたみボタン（スマホのみ）
    const togglePreviewButton = document.getElementById('togglePreviewButton');
    if (togglePreviewButton) {
        togglePreviewButton.addEventListener('click', togglePreview);
    }
    
    // プレビューペインのリサイズハンドル（スマホのみ）
    const previewResizeHandle = document.getElementById('previewResizeHandle');
    if (previewResizeHandle) {
        previewResizeHandle.addEventListener('mousedown', startPreviewResize);
        previewResizeHandle.addEventListener('touchstart', startPreviewResize, { passive: false });
    }
    
    // プレビューペインの高さを復元
    setTimeout(() => {
        restorePreviewHeightLocal();
    }, 100);
    
    // トグルアイコンの初期状態を設定（スマホで表示されている場合）
    const toggleIcon = document.getElementById('previewToggleIcon');
    if (toggleIcon) {
        const previewPane = document.getElementById('previewPane');
        if (previewPane && !previewPane.classList.contains('hidden')) {
            toggleIcon.classList.add('rotate-180');
        }
    }
    
    // Treeインポートボタン
    const importTreeBtn = document.getElementById('importTreeButton');
    if (importTreeBtn) {
        importTreeBtn.addEventListener('click', importTreeOutput);
    }
    
    // アプリクリアボタン
    const clearAppsBtn = document.getElementById('clearAppsButton');
    if (clearAppsBtn) {
        clearAppsBtn.addEventListener('click', clearApps);
    }
    
    // テーブルエディタを閉じるボタン
    const tableEditorCloseBtn = document.getElementById('tableEditorClose');
    const tableEditorBackdrop = document.getElementById('tableEditorBackdrop');
    if (tableEditorCloseBtn) {
        tableEditorCloseBtn.addEventListener('click', closeTableEditor);
    }
    if (tableEditorBackdrop) {
        tableEditorBackdrop.addEventListener('click', closeTableEditor);
    }
    
    // 現在のPCインジケーターを初期化
    setTimeout(() => {
        updateCurrentPCIndicator();
    }, 100);
    
    // 言語切り替え
    const languageSelect = document.getElementById('languageSelect') as HTMLSelectElement | null;
    const languageLabel = document.getElementById('languageLabel');
    if (languageSelect) {
        languageSelect.value = getCurrentLanguage();
        languageSelect.addEventListener('change', (e) => {
            const target = e.target as HTMLSelectElement;
            setLanguage(target.value);
        });
    }
    if (languageLabel) {
        // 言語変更時にラベルも更新
        window.addEventListener('languageChanged', () => {
            languageLabel.textContent = t('language');
        });
    }

    // 左カラムのスクロール位置を監視・保存
    const leftContainer = document.getElementById('settingsContainer');
    if (leftContainer) {
        leftContainer.addEventListener('scroll', () => {
            saveLeftScrollLocal();
        });
    }
    
    // 広告読み込み
    loadAds();
    
    // 言語変更イベントリスナー
    window.addEventListener('languageChanged', async (e) => {
        // HTMLのlang属性を更新
        const htmlLangElement = document.getElementById('htmlLang') as HTMLHtmlElement | null;
        const htmlLang = htmlLangElement || document.documentElement;
        const customEvent = e as CustomEvent<{ lang: string }>;
        htmlLang.lang = customEvent.detail.lang;
        updateUITexts();
        renderUI();
        // テーブルエディタ（モーダル/フル画面）も文言が変わるので再描画
        renderTable();
        restoreSelectionStateLocal();
        restoreCategoryStateLocal();
        restoreVariableValuesLocal();
        updateCode();
    });

    // 別タブで言語が変更された場合も追従（storageイベントは他タブでのみ発火）
    window.addEventListener('storage', (e) => {
        if (e.key === 'winbatchgen_language' && e.newValue) {
            setLanguage(e.newValue);
        }
    });
    
    // 初期UIテキストを更新
    updateUITexts();
    
    // HTMLのlang属性を初期化
    const htmlLangElement = document.getElementById('htmlLang') as HTMLHtmlElement | null;
    const htmlLang = htmlLangElement || document.documentElement;
    htmlLang.lang = getCurrentLanguage();

    // フル画面テーブルエディタビュー（table-editor.html）の場合はテーブルを自動レンダリング
    const tableEditorContainer = document.getElementById('tableEditorContainer');
    const tableEditorModal = document.getElementById('tableEditorModal');
    if (tableEditorContainer && !tableEditorModal) {
        renderTable();
    }

    // ヘッダーの現在PC移動ボタン・インジケーター
    const currentPCPrevButton = document.getElementById('currentPCPrevButton');
    const currentPCNextButton = document.getElementById('currentPCNextButton');
    const currentPCIndicator = document.getElementById('currentPCIndicator');
    if (currentPCPrevButton) {
        currentPCPrevButton.addEventListener('click', (e) => {
            e.stopPropagation();
            selectPreviousPC();
        });
    }
    if (currentPCNextButton) {
        currentPCNextButton.addEventListener('click', (e) => {
            e.stopPropagation();
            selectNextPC();
        });
    }
    if (currentPCIndicator) {
        currentPCIndicator.addEventListener('click', () => {
            openTableEditor();
        });
    }

    // 別タブから現在PCやテーブル内容が変更された場合にメイン画面も追従させる
    window.addEventListener('storage', (e) => {
        // メイン画面が存在するタブだけ反応（table-editor.html では何もしない）
        const isMainView = !!document.getElementById('settingsContainer');
        if (!isMainView) return;

        // 現在PCの選択変更
        if (e.key === CURRENT_PC_STORAGE_KEY) {
            const newRowId = e.newValue;
            if (newRowId) {
                // 外部変更でも通常と同じ処理でPCを反映
                selectPC(newRowId);
            } else {
                // 選択解除された場合はデフォルト状態に戻す
                restoreMainViewToDefault();
            }
            return;
        }

        // テーブル内容の変更（チェックボックスや変数編集など）
        if (e.key === TABLE_DATA_STORAGE_KEY) {
            const currentRowId = getCurrentPCRowId();
            if (currentRowId) {
                // 「現在選択中のPC」の状態をメイン画面に再適用
                selectPC(currentRowId);
            } else {
                restoreMainViewToDefault();
            }
        }
    });
    
    // リサイズ時に広告を再読み込み、デスクトップ表示ではスタイルをリセット
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    window.addEventListener('resize', () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (AD_CONFIG.enabled) {
                loadAds();
            }
            // デスクトップ表示になった場合、右ペインのスタイルをリセット
            const previewPane = document.getElementById('previewPane');
            if (previewPane && window.matchMedia('(min-width: 768px)').matches) {
                previewPane.style.flex = '';
                previewPane.style.flexShrink = '';
                previewPane.style.height = '';
                previewPane.style.marginTop = '';
            }
        }, 300);
    });
    
    // UI生成後に警告表示を更新（初回はハイライト不要）
    setTimeout(() => {
        updateCode(false);
    }, 0);

    // Docsドロワーのクローズ処理
    const docsClose = document.getElementById('docsDrawerClose');
    const docsBackdrop = document.getElementById('docsDrawerBackdrop');
    if (docsClose) {
        docsClose.addEventListener('click', closeDocs);
    }
    if (docsBackdrop) {
        docsBackdrop.addEventListener('click', closeDocs);
    }
}

declare global {
    interface Window {
        app: {
            toggleSection: (sectionId: string) => void;
            toggleCategory: (categoryId: string) => void;
            togglePreview: () => void;
            updateCode: (withHighlight?: boolean) => void;
            importTreeOutput: () => void;
            clearApps: () => void;
            copyToClipboard: () => void;
            downloadBatchFile: () => void;
            downloadRevertBatchFile: () => void;
            filterSettings: (searchTerm: string) => void;
            toggleAllSections: (expand: boolean) => void;
            openDocs: (itemId: string) => void;
            saveVariableValue: (itemId: string, varName: string, value: string) => void;
            updateCodePreview: (itemId: string) => void;
            addNIC: (itemId: string) => void;
            removeNIC: (itemId: string, index: number) => void;
            updateNICConfig: (itemId: string, index: number) => void;
            updateGatewayPlaceholder: (itemId: string, index: number) => void;
            showGatewayAutocomplete: (itemId: string, index: number) => void;
            hideGatewayAutocomplete: (itemId: string, index: number) => void;
            applyGatewayAutocomplete: (itemId: string, index: number, value: string) => void;
            handleGatewayKeyDown: (itemId: string, index: number, event: KeyboardEvent) => void;
            addTableRow: () => void;
            removeTableRow: (rowId: string) => void;
            updateTablePCName: (rowId: string, pcName: string) => void;
            updateTableCheckbox: (rowId: string, itemId: string, checked: boolean) => void;
            updateTableVariable: (rowId: string, itemId: string, varName: string, value: string) => void;
            selectTablePC: (rowId: string) => void;
            generateBatchFilesFromTable: () => void;
            clearTableData: () => void;
            openTableEditor: () => void;
            closeTableEditor: () => void;
            selectPreviousPC: () => void;
            selectNextPC: () => void;
        };
        adsbygoogle?: any[];
        marked?: {
            parse: (markdown: string) => string;
            setOptions: (options: any) => void;
        };
    }
}

// グローバルに公開（HTMLのonclickから呼び出すため）
window.app = {
    toggleSection,
    toggleCategory,
    togglePreview,
    updateCode,
    importTreeOutput,
    clearApps,
    copyToClipboard,
    downloadBatchFile,
    downloadRevertBatchFile,
    filterSettings,
    toggleAllSections,
    openDocs,
    saveVariableValue: saveVariableValueLocal,
    updateCodePreview,
    addNIC,
    removeNIC,
    updateNICConfig,
    updateGatewayPlaceholder,
    showGatewayAutocomplete,
    hideGatewayAutocomplete,
    applyGatewayAutocomplete,
    handleGatewayKeyDown,
    addTableRow: addRow,
    removeTableRow: removeRow,
    updateTablePCName: updatePCName,
    updateTableCheckbox: updateCheckbox,
    updateTableVariable: updateVariable,
    selectTablePC: selectPC,
    generateBatchFilesFromTable,
    clearTableData,
    openTableEditor,
    closeTableEditor,
    selectPreviousPC,
    selectNextPC
};
